import React, { Children, useRef, useState } from 'react';

import { Divider, Grid, Paper, Popover } from '@mui/material';
import clsx from 'clsx';
import { SelectButton } from 'src/components';
import { VertDotsIcon } from 'src/components/icons/VertDotsIcon';
import { makeStyles } from 'tss-react/mui';

import { useVisibleAndOverflowedButtons } from './useVisibleAndOverflowedButtons';

const useStyles = makeStyles()(theme => ({
  divider: {
    height: 24,
    marginLeft: theme.spacing(0.8),
    marginRight: theme.spacing(0.8),
  },
  hiddenOverflowTrigger: {
    left: 0,
    pointerEvents: 'none',
    position: 'absolute',
    top: 0,
    visibility: 'hidden',
  },
  overflowMenuWrapper: {
    alignItems: 'center',
    display: 'flex',
    flexWrap: 'nowrap',
    position: 'relative',
    width: '100%',
  },
  paper: {
    display: 'flex',
    padding: theme.spacing(1),
    zIndex: theme.zIndex.tooltip,
  },
  toolbarButton: {
    alignItems: 'center',
    display: 'flex',
  },
  /**
   * Visually hide the toolbar button without affecting its width, so that we can determine which
   * buttons needs to be moved to the overflow menu.
   */
  toolbarButtonHidden: {
    height: 0,
    left: 0,
    overflow: 'hidden',
    pointerEvents: 'none',
    position: 'absolute',
    top: 0,
    visibility: 'hidden',
  },
  wrapper: {
    alignItems: 'center',
    display: 'flex',
  },
}));

interface ButtonsBarWithOverflowMenuProps {
  children: React.ReactNode;
}

export function ButtonsBarWithOverflowMenu({ children }: ButtonsBarWithOverflowMenuProps) {
  const { classes } = useStyles();
  const containerRef = useRef<HTMLDivElement>(null);
  const childrenArray = Children.toArray(children);

  const { visibleElementsCount } = useVisibleAndOverflowedButtons({
    childCount: childrenArray.length,
    containerRef,
  });

  const adjustedVisibleCount = getAdjustedVisibleCount(childrenArray, visibleElementsCount);
  const overflowedElements = filterFirstDivider(childrenArray.slice(visibleElementsCount));
  const hasOverflow = overflowedElements.length > 0;

  return (
    <>
      <Grid
        ref={containerRef}
        container
        className={classes.overflowMenuWrapper}
        sx={{
          alignItems: 'center',
        }}
      >
        {childrenArray.map((child, index) => {
          const isHidden = index >= adjustedVisibleCount;

          return (
            <div
              key={index}
              data-toolbar-item
              aria-hidden={isHidden}
              className={clsx(classes.toolbarButton, isHidden && classes.toolbarButtonHidden)}
            >
              {child}
            </div>
          );
        })}

        <OverflowedButtonsPopover hidden={!hasOverflow}>
          {overflowedElements}
        </OverflowedButtonsPopover>
      </Grid>
    </>
  );
}

interface OverflowedButtonsPopoverProps {
  children: React.ReactNode;
  hidden: boolean;
}

function OverflowedButtonsPopover({ children, hidden }: OverflowedButtonsPopoverProps) {
  const { classes } = useStyles();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  if (hidden && open) setOpen(false);

  return (
    <div
      data-toolbar-overflow-wrapper
      aria-hidden={hidden}
      className={clsx(classes.wrapper, hidden && classes.hiddenOverflowTrigger)}
    >
      <Divider className={classes.divider} orientation="vertical" />
      <SelectButton
        ref={buttonRef}
        aria-label="More toolbar items"
        data-cy="overflow-menu-toolbar-button"
        disabled={hidden}
        selected={!hidden && open}
        onClick={() => setOpen(prev => !prev)}
      >
        <VertDotsIcon />
      </SelectButton>
      <Popover
        anchorEl={buttonRef.current}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        open={open}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        onClose={() => setOpen(false)}
      >
        <Paper className={classes.paper}>
          <Grid
            container
            sx={{
              alignItems: 'center',
            }}
          >
            {children}
          </Grid>
        </Paper>
      </Popover>
    </div>
  );
}

/**
 * Adjust the count to be no greater than childrenArray.length, and so that the last visible element
 * is not a divider.
 */
function getAdjustedVisibleCount(childrenArray: React.ReactNode[], baseCount: number) {
  const lastNonDividerIndex = childrenArray
    .slice(0, baseCount)
    .findLastIndex(node => !isDividerElement(node));

  return lastNonDividerIndex + 1;
}

function filterFirstDivider(elements: React.ReactNode[]) {
  if (elements.length === 0) return elements;
  return isDividerElement(elements[0]) ? elements.slice(1) : elements;
}

function isDividerElement(node: React.ReactNode) {
  return React.isValidElement(node) && node.type === Divider;
}
